import { profileRemoteFeedback, type ProfileRemoteFeedback } from "../i18n/profileFeedbackPresentation";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

export type PasskeyMessageCode = "browserRequired" | "secureContextRequired" | "webAuthnUnavailable"
  | "passkeysUnavailable" | "creationCanceled" | "domainNotAllowed" | "credentialExists"
  | "deviceUnsupported" | "authenticatorFailed" | "operationFailed" | "registrationStartFailed"
  | "optionsUnavailable" | "verificationFailed";

export type PasskeyLoginResult = {
  feedback?: ProfileRemoteFeedback;
  ok: boolean;
  message?: string;
  code?: PasskeyMessageCode;
  redirectTo?: string;
};

export type PasskeySupportDetails = {
  secureContext: boolean;
  publicKeyCredential: boolean;
  platformAuthenticatorAvailable?: boolean;
  conditionalMediationAvailable?: boolean;
};

export type PasskeySupport = {
  supported: boolean;
  reason?: string;
  code?: PasskeyMessageCode;
  details: PasskeySupportDetails;
};

const base64UrlFromBytes = (bytes: Uint8Array): string => {
  const binary = Array.from(bytes, (value) => String.fromCharCode(value)).join("");
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const toBase64Url = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (value instanceof ArrayBuffer) {
    return base64UrlFromBytes(new Uint8Array(value));
  }
  if (ArrayBuffer.isView(value)) {
    return base64UrlFromBytes(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
  }
  if (typeof value === "object") {
    const record = value as { type?: unknown; data?: unknown };
    if (record.type === "Buffer" && Array.isArray(record.data)) {
      return base64UrlFromBytes(new Uint8Array(record.data));
    }
  }
  return undefined;
};

const normalizeAuthOptions = (options: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = { ...options };
  const challenge = toBase64Url(options.challenge);
  if (challenge) {
    normalized.challenge = challenge;
  }

  if (Array.isArray(options.allowCredentials)) {
    normalized.allowCredentials = options.allowCredentials.map((entry) => {
      if (!entry || typeof entry !== "object") return entry;
      const record = entry as { id?: unknown; type?: unknown };
      const id = toBase64Url(record.id) ?? record.id;
      return {
        ...record,
        id,
        type: record.type ?? "public-key",
      };
    });
  }

  return normalized;
};

const describeWebauthnError = (error: unknown): Pick<PasskeyLoginResult, "message" | "code"> => {
  if (error instanceof DOMException) {
    switch (error.name) {
      case "NotAllowedError":
        return { code: "creationCanceled", message: "Passkey creation was canceled or timed out." };
      case "SecurityError":
        return { code: "domainNotAllowed", message: "Passkeys are not allowed for this domain. Check HTTPS and RP ID settings." };
      case "InvalidStateError":
        return { code: "credentialExists", message: "A passkey already exists for this account on this device." };
      case "NotSupportedError":
        return { code: "deviceUnsupported", message: "Passkeys are not supported on this device." };
      case "ConstraintError":
        return { code: "authenticatorFailed", message: "Passkey creation failed. Try a different authenticator." };
      default:
        if (error.message) return { message: error.message };
        break;
    }
  }
  if (error instanceof Error) {
    if (error.message) return { message: error.message };
  }
  return { code: "operationFailed", message: "Passkey operation failed." };
};

export const getPasskeySupport = async (): Promise<PasskeySupport> => {
  if (typeof window === "undefined") {
    return {
      supported: false,
      reason: "Passkeys require a browser context.",
      code: "browserRequired",
      details: { secureContext: false, publicKeyCredential: false },
    };
  }

  const secureContext = window.isSecureContext;
  const publicKeyCredential = typeof window.PublicKeyCredential !== "undefined";

  if (!secureContext) {
    return {
      supported: false,
      reason: "Passkeys require a secure context (HTTPS or localhost).",
      code: "secureContextRequired",
      details: { secureContext, publicKeyCredential },
    };
  }

  if (!publicKeyCredential) {
    return {
      supported: false,
      reason: "WebAuthn is not available in this browser.",
      code: "webAuthnUnavailable",
      details: { secureContext, publicKeyCredential },
    };
  }

  let platformAuthenticatorAvailable: boolean | undefined;
  let conditionalMediationAvailable: boolean | undefined;

  if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function") {
    try {
      platformAuthenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      platformAuthenticatorAvailable = undefined;
    }
  }

  if (typeof PublicKeyCredential.isConditionalMediationAvailable === "function") {
    try {
      conditionalMediationAvailable = await PublicKeyCredential.isConditionalMediationAvailable();
    } catch {
      conditionalMediationAvailable = undefined;
    }
  }

  return {
    supported: true,
    details: {
      secureContext,
      publicKeyCredential,
      platformAuthenticatorAvailable,
      conditionalMediationAvailable,
    },
  };
};

export const passkeyLogin = async (username?: string, remember?: boolean): Promise<PasskeyLoginResult> => {
  const support = await getPasskeySupport();
  if (!support.supported) {
    return { ok: false, message: support.reason ?? "Passkeys are not available on this device." };
  }

  const optionsRes = await fetch("/api/auth/webauthn/login/options", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });

  if (!optionsRes.ok) {
    const data = await optionsRes.json().catch(() => ({}));
    return { ok: false, message: data?.message ?? "Unable to start passkey login." };
  }

  const optionsPayload = await optionsRes.json();
  if (!optionsPayload?.options || !optionsPayload?.challengeId) {
    return { ok: false, message: "Passkey options unavailable." };
  }

  const rawOptions = optionsPayload.options?.publicKey ?? optionsPayload.options;
  const normalizedOptions = normalizeAuthOptions(rawOptions as Record<string, unknown>) as unknown as Parameters<
    typeof startAuthentication
  >[0]["optionsJSON"];
  const credential = await startAuthentication({ optionsJSON: normalizedOptions });

  const verifyRes = await fetch("/api/auth/webauthn/login/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      challengeId: optionsPayload.challengeId,
      credential,
      remember,
    }),
  });

  const verifyBody = await verifyRes.json().catch(() => ({})) as { message?: string; redirectTo?: string };

  if (!verifyRes.ok) {
    return { ok: false, message: verifyBody?.message ?? "Passkey verification failed." };
  }

  return { ok: true, redirectTo: verifyBody?.redirectTo };
};

export const passkeyRegister = async (csrfToken: string, name?: string): Promise<PasskeyLoginResult> => {
  const support = await getPasskeySupport();
  if (!support.supported) {
    return { ok: false, code: support.code ?? "passkeysUnavailable", message: support.reason ?? "Passkeys are not available on this device." };
  }

  const optionsRes = await fetch("/api/auth/webauthn/register/options", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({}),
  });

  if (!optionsRes.ok) {
    const data = await optionsRes.json().catch(() => ({}));
    return { ok: false, feedback: profileRemoteFeedback(data), code: data?.message ? undefined : "registrationStartFailed", message: data?.message ?? "Unable to start passkey registration." };
  }

  const optionsPayload = await optionsRes.json();
  if (!optionsPayload?.options || !optionsPayload?.challengeId) {
    return { ok: false, code: "optionsUnavailable", message: "Passkey options unavailable." };
  }

  let credential: unknown;
  try {
    credential = await startRegistration({
      optionsJSON: optionsPayload.options as unknown as Parameters<typeof startRegistration>[0]["optionsJSON"],
    });
  } catch (error) {
    return { ok: false, ...describeWebauthnError(error) };
  }

  const verifyRes = await fetch("/api/auth/webauthn/register/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({
      challengeId: optionsPayload.challengeId,
      credential,
      name,
    }),
  });

  if (!verifyRes.ok) {
    const data = await verifyRes.json().catch(() => ({}));
    return { ok: false, feedback: profileRemoteFeedback(data), code: data?.message ? undefined : "verificationFailed", message: data?.message ?? "Passkey verification failed." };
  }

  return { ok: true };
};
